import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { toast } from "sonner";

const KEY_DONE = "vs_install_nudge";
const KEY_SESSIONS = "vs_sessions";
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
const isMobile = () => window.matchMedia?.("(max-width: 767px)")?.matches;

// One-time "Install VirtuStars" banner, shown on mobile after the user's first session.
export function InstallNudge() {
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    // Count one session per browser launch
    if (!sessionStorage.getItem("vs_sess_counted")) {
      sessionStorage.setItem("vs_sess_counted", "1");
      const n = Number(localStorage.getItem(KEY_SESSIONS) || "0") + 1;
      localStorage.setItem(KEY_SESSIONS, String(n));
    }
    const sessions = Number(localStorage.getItem(KEY_SESSIONS) || "0");
    const done = localStorage.getItem(KEY_DONE) === "done";
    if (!done && sessions >= 2 && isMobile() && !isStandalone()) {
      const t = setTimeout(() => { setShow(true); localStorage.setItem(KEY_DONE, "done"); }, 1200);
      return () => clearTimeout(t);
    }
  }, []);

  if (!show) return null;

  const dismiss = () => setShow(false);
  const install = async () => {
    const dp = window.deferredInstallPrompt;
    if (dp) {
      dp.prompt();
      const { outcome } = await dp.userChoice;
      if (outcome === "accepted") toast.success("Installing VirtuStars…");
      window.deferredInstallPrompt = null;
      setShow(false);
      return;
    }
    if (isIOS()) { setIosHelp(true); return; }
    toast("Open your browser menu and choose Install / Add to Home Screen");
    setShow(false);
  };

  return (
    <>
      <div className="md:hidden fixed left-3 right-3 bottom-20 z-50 surface glass-gold rounded-2xl p-3 flex items-center gap-3 shadow-2xl animate-in slide-in-from-bottom-4" data-testid="install-nudge">
        <div className="w-10 h-10 rounded-xl bg-gold/15 flex items-center justify-center shrink-0"><Download size={18} className="text-gold" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm">Install VirtuStars</div>
          <div className="text-xs text-muted-foreground">Add the app to your home screen for the full experience.</div>
        </div>
        <button onClick={install} className="btn-gold rounded-full px-3 py-1.5 text-xs font-semibold shrink-0" data-testid="install-nudge-btn">Install</button>
        <button onClick={dismiss} className="text-muted-foreground p-1 shrink-0" data-testid="install-nudge-dismiss"><X size={16} /></button>
      </div>
      {iosHelp && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-end justify-center p-4" onClick={() => { setIosHelp(false); setShow(false); }} data-testid="install-nudge-ios">
          <div className="surface rounded-2xl p-6 w-full max-w-sm relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setIosHelp(false); setShow(false); }} className="absolute top-4 right-4 text-muted-foreground"><X size={18} /></button>
            <div className="flex items-center gap-2 mb-2"><Download size={18} className="text-gold" /><h3 className="font-display font-bold text-lg">Add to Home Screen</h3></div>
            <ol className="text-sm text-muted-foreground space-y-2 mt-2">
              <li>1. Tap the <span className="text-white">Share</span> button in Safari</li>
              <li>2. Choose <span className="text-white">Add to Home Screen</span></li>
              <li>3. Tap <span className="text-white">Add</span></li>
            </ol>
          </div>
        </div>
      )}
    </>
  );
}
